import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, Shield, Trash2, Lock, Eye, EyeOff, Users, ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_protected: true
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [creating, setCreating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/administrators`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error al cargar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/administrators`, formData);
      toast.success('Administrador creado exitosamente');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', is_protected: true });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear administrador');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (adminId, isProtected) => {
    if (isProtected) {
      toast.error('Esta cuenta está protegida y no puede ser eliminada');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este administrador?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/administrators/${adminId}`);
      toast.success('Administrador eliminado');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar administrador');
    }
  };

  const toggleProtection = async (adminId, currentStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/administrators/${adminId}/protection`, {
        is_protected: !currentStatus
      });
      toast.success(currentStatus ? 'Protección removida' : 'Cuenta protegida');
      fetchAdmins();
    } catch (error) {
      toast.error('Error al cambiar protección');
    }
  };

  const openPasswordModal = (admin) => {
    setSelectedAdmin(admin);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/administrators/${selectedAdmin.user_id}/password`, {
        new_password: passwordData.new_password
      });
      toast.success('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
      setSelectedAdmin(null);
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-pink-500" />
                Gestión de Administradores
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Crea y gestiona cuentas de administrador
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-pink-600 hover:bg-pink-500 text-white"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Nuevo Admin
          </Button>
        </div>

        {/* Admin List */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-500" />
              Administradores ({admins.length})
            </h2>
          </div>

          {admins.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-lg">No hay administradores creados</p>
              <p className="text-zinc-600 text-sm mt-2">Crea tu primera cuenta de administrador</p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 bg-pink-600 hover:bg-pink-500"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Crear Administrador
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {admins.map((admin) => (
                <div key={admin.user_id} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${admin.is_protected ? 'bg-green-500/20' : 'bg-zinc-800'}`}>
                      {admin.is_protected ? (
                        <ShieldCheck className="w-6 h-6 text-green-500" />
                      ) : (
                        <Shield className="w-6 h-6 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        {admin.name}
                        {admin.is_protected && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                            Protegido
                          </span>
                        )}
                      </p>
                      <p className="text-zinc-500 text-sm">{admin.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPasswordModal(admin)}
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      title="Cambiar contraseña"
                    >
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleProtection(admin.user_id, admin.is_protected)}
                      className={`${admin.is_protected ? 'text-green-400 hover:text-green-300' : 'text-zinc-400 hover:text-white'}`}
                      title={admin.is_protected ? 'Quitar protección' : 'Proteger cuenta'}
                    >
                      <Lock className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAdmin(admin.user_id, admin.is_protected)}
                      className={`${admin.is_protected ? 'text-zinc-600 cursor-not-allowed' : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'}`}
                      disabled={admin.is_protected}
                      title={admin.is_protected ? 'Cuenta protegida' : 'Eliminar administrador'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">📋 Instrucciones</h3>
          <ol className="text-zinc-400 text-sm space-y-2 list-decimal list-inside">
            <li>Para crear un nuevo administrador, haz clic en "Nuevo Admin"</li>
            <li>Usa el ícono <span className="text-blue-400">🔑</span> para cambiar la contraseña de cualquier administrador</li>
            <li>Las cuentas marcadas como "Protegidas" no pueden ser eliminadas</li>
            <li>Solo los administradores pueden acceder al panel de administración</li>
          </ol>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-pink-500" />
              Crear Administrador
            </h2>
            
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                  placeholder="Ej: Karen García"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                  placeholder="admin@bootyandlegs.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none pr-12"
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-xl">
                <input
                  type="checkbox"
                  id="is_protected"
                  checked={formData.is_protected}
                  onChange={(e) => setFormData({ ...formData, is_protected: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-600 text-pink-500 focus:ring-pink-500 bg-zinc-700"
                />
                <label htmlFor="is_protected" className="text-zinc-300 text-sm">
                  <span className="font-medium">Proteger cuenta</span>
                  <span className="text-zinc-500 block">No podrá ser eliminada</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 text-zinc-400 hover:text-white border border-zinc-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-pink-600 hover:bg-pink-500 text-white"
                >
                  {creating ? 'Creando...' : 'Crear Admin'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-500" />
              Cambiar Contraseña
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Cambiando contraseña para: <span className="text-white font-medium">{selectedAdmin.name}</span>
            </p>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none pr-12"
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Confirmar contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
                  placeholder="Repite la contraseña"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedAdmin(null);
                  }}
                  className="flex-1 text-zinc-400 hover:text-white border border-zinc-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                >
                  {changingPassword ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
